import React, { useState, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  MapPin,
  List,
  Map as MapIcon,
  X,
  Building2,
  Sparkles,
  Layers,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicContent, fetchPublicSettings, makeSectionCopyPicker } from '../services/publicContentService';

// MapTiler public API key — set via REACT_APP_MAPTILER_KEY in the frontend
// .env. Restrict allowed HTTP referrers on the MapTiler dashboard for safety.
const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY || '';

// Warm gold/orange EBC marker — light, premium, environmental.
// `count` renders a small badge for regions that contain more than one project.
const createMarkerIcon = (active = false, count = 1) => {
  const size = active ? 40 : 32;
  const isCluster = count > 1;
  const badge = isCluster
    ? `<div style="position:absolute;top:-6px;right:-6px;min-width:20px;height:20px;padding:0 6px;
                border-radius:9999px;background:#0f172a;color:#fde68a;
                font-size:11px;font-weight:700;font-family:inherit;
                display:flex;align-items:center;justify-content:center;
                border:2px solid #ffffff;box-shadow:0 2px 6px rgba(0,0,0,0.25);">
         ${count}
       </div>`
    : '';
  const html = `
    <div style="position:relative;width:${size}px;height:${size}px;">
      <div style="position:absolute;inset:-8px;border-radius:9999px;background:radial-gradient(circle, rgba(245,135,32,0.32), rgba(245,135,32,0) 70%);"></div>
      <div style="position:relative;width:${size}px;height:${size}px;border-radius:9999px;
                  background:linear-gradient(135deg,#fbbf24 0%,#f58720 55%,#dc7415 100%);
                  display:flex;align-items:center;justify-content:center;
                  border:2px solid #ffffff;
                  box-shadow:0 4px 12px rgba(245,135,32,0.35), 0 0 0 1px rgba(245,135,32,0.18);">
        <svg width="${Math.round(size * 0.5)}" height="${Math.round(size * 0.5)}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
          <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"></path>
          <path d="M2 21c0-3 1.85-5.36 5.08-6"></path>
        </svg>
      </div>
      ${badge}
    </div>
  `;
  return L.divIcon({
    html,
    className: 'ebc-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Auto-fly to selected project / region
const FlyTo = ({ position, zoom = 7 }) => {
  const map = useMap();
  React.useEffect(() => {
    if (position) {
      map.flyTo(position, zoom, { duration: 1.2 });
    }
  }, [position, zoom, map]);
  return null;
};

/**
 * InvalidateOnResize
 *
 * The map container is inside a CSS grid that switches from 12 cols (map only)
 * to 8+4 cols (map + side panel). Leaflet caches tile positions and needs
 * `map.invalidateSize()` whenever the container width changes, otherwise tiles
 * don't re-tile and a white strip appears on the newly-exposed edge. This tiny
 * hook watches the resize signal and asks Leaflet to recompute.
 */
const InvalidateOnResize = ({ trigger }) => {
  const map = useMap();
  React.useEffect(() => {
    // Small delay lets the CSS grid finish animating before recomputation.
    const t1 = setTimeout(() => map.invalidateSize({ animate: false }), 350);
    const t2 = setTimeout(() => map.invalidateSize({ animate: false }), 900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [trigger, map]);
  return null;
};

/**
 * Simplified Saudi Arabia outline (~30 vertices, clockwise).
 *
 * Used as a hole inside the country-mask polygon so that everything OUTSIDE
 * Saudi Arabia is covered by a soft light-beige overlay — this visually hides
 * the country-name labels of surrounding countries (Iraq, Iran, UAE, Jordan,
 * Egypt, Yemen, …) that are baked into the MapTiler raster tiles, while the
 * cities, provinces, roads, and geography INSIDE Saudi Arabia stay fully
 * visible with their original labels.
 *
 * Approximate national border — good enough for a soft visual mask.
 */
const SAUDI_OUTLINE = [
  [29.20, 34.95], // NW (Gulf of Aqaba tip)
  [29.35, 36.20],
  [29.34, 36.75],
  [29.20, 37.60],
  [30.00, 37.99],
  [31.30, 38.90],
  [32.15, 39.10],
  [31.80, 40.70],
  [30.95, 41.30],
  [29.80, 42.80],
  [29.10, 44.70],
  [29.10, 46.55],
  [28.55, 48.42],
  [28.10, 48.55],
  [27.60, 49.85],
  [26.90, 50.20],
  [26.10, 50.35],
  [25.65, 50.55],
  [25.15, 51.10],
  [24.60, 51.60],
  [24.20, 52.30],
  [23.10, 55.20],
  [22.60, 55.30],
  [20.55, 55.60],
  [19.30, 55.66],
  [18.10, 51.90],
  [17.50, 47.30],
  [17.30, 44.20],
  [16.60, 43.15],
  [16.90, 42.75],
  [17.60, 42.85],
  [18.60, 41.60],
  [19.90, 41.35],
  [21.35, 39.85],
  [22.30, 39.05],
  [24.15, 38.10],
  [25.35, 37.05],
  [26.85, 36.10],
  [27.35, 35.55],
  [28.10, 34.86],
  [29.20, 34.95], // close
];

/**
 * Large outer ring that will act as the "world" polygon; combined with the
 * Saudi outline hole, this becomes a mask covering only areas outside Saudi
 * Arabia. Kept generous so it fully covers the panning bounds without needing
 * to be updated when bounds change.
 */
const WORLD_MASK_RING = [
  [-10, 0],
  [-10, 90],
  [50, 90],
  [50, 0],
];

const FALLBACK_PROJECTS = [
  {
    id: 1,
    name: { ar: 'تقديم خدمات استشارية لموسوعة أرامكو للطيور', en: 'Providing Consultancy Services for the Saudi Aramco Birds Encyclopedia' },
    client: { ar: 'شركة النفط السعودية (أرامكو)', en: 'Saudi Arabian Oil Company (Saudi Aramco)' },
    region: { ar: 'المملكة العربية السعودية', en: 'Kingdom of Saudi Arabia' },
    coords: [26.2885, 50.0991],
    summary: {
      ar: 'مشروع متخصص لتوثيق التنوع الطيري في المملكة، تضمّن جمع البيانات الميدانية، والتصنيف العلمي، وإعداد مرجع علمي شامل لطيور المنطقة.',
      en: 'A specialised project documenting Saudi avian biodiversity through field surveys, scientific classification, and the development of a comprehensive ornithological reference.',
    },
  },
  {
    id: 2,
    name: { ar: 'دراسة تقييم الأثر البيئي والتصريح البيئي للتشغيل', en: 'Environmental Impact Assessment (EIA) Study and Securing the Environmental Operation Permit' },
    client: { ar: 'شركة مرافق البحر الأحمر', en: 'Red Sea Utilities Company' },
    region: { ar: 'مشروع البحر الأحمر – أملج', en: 'The Red Sea Project, Umluj' },
    coords: [25.31, 37.04],
    summary: {
      ar: 'إعداد دراسة تقييم الأثر البيئي الكاملة والحصول على التصريح البيئي اللازم للتشغيل ضمن مشروع البحر الأحمر السياحي.',
      en: 'Full Environmental Impact Assessment and securing operational permits for utilities within the Red Sea tourism project.',
    },
  },
  {
    id: 3,
    name: { ar: 'إعادة تأهيل مناطق تعشيش السلاحف بجزر فرسان والليث', en: 'Rehabilitation of Turtle Nesting Sites on Perim, Farasan, and Al-Lith Islands' },
    client: { ar: 'المركز الوطني لتنمية الحياة الفطرية', en: 'National Center for Wildlife (NCW)' },
    region: { ar: 'فرسان – الليث', en: 'Perim – Farasan – Al-Lith' },
    coords: [16.7, 41.95],
    summary: {
      ar: 'تنفيذ برنامج علمي وميداني لإعادة تأهيل شواطئ تعشيش السلاحف البحرية المهددة، شمل المسح، والترميم، والمراقبة المستمرة.',
      en: 'A scientific and field programme rehabilitating endangered marine turtle nesting beaches — including survey, restoration and ongoing monitoring.',
    },
  },
  {
    id: 4,
    name: { ar: 'تقييم الأثر البيئي لثلاثة مواقع للاستزراع المائي', en: 'Environmental Impact Assessment (EIA) Study for Three Aquaculture Project Sites in Medina' },
    client: { ar: 'وزارة البيئة والمياه والزراعة', en: 'Ministry of Environment, Water and Agriculture (MEWA)' },
    region: { ar: 'المدينة المنورة', en: 'Al-Madinah Al-Munawwarah' },
    coords: [24.4686, 39.6142],
    summary: {
      ar: 'دراسة تقييم الأثر البيئي لثلاثة مواقع استزراع مائي، تضمّنت تحليل جودة المياه، والأثر على البيئة المحيطة، ووضع خطط الإدارة البيئية.',
      en: 'EIA across three aquaculture sites — water-quality analysis, ecosystem impact, and environmental management planning.',
    },
  },
  {
    id: 5,
    name: { ar: 'دراسة تأثير أعمال الحفر البحري قرب جزيرة جبل حسان', en: 'Assessing the Impacts of Offshore Drilling Operations 9 Nautical Miles off the Coasts of Umluj City, near Jabal Hassan Island' },
    client: { ar: 'المركز الوطني للرقابة على الالتزام البيئي', en: 'National Center for Environmental Compliance (NCEC)' },
    region: { ar: 'أملج', en: 'Umluj' },
    coords: [25.04, 37.27],
    summary: {
      ar: 'دراسة تقييم تأثير أعمال الحفر داخل البحر على بُعد 9 أميال بحرية من شواطئ أملج، مع توصيات للحدّ من الأثر على البيئة البحرية.',
      en: 'Assessment of marine drilling impacts 9 nautical miles off Umluj, with recommendations to minimise impact on the marine environment.',
    },
  },
  {
    id: 6,
    name: { ar: 'تدريب واستزراع الشعاب المرجانية لمشروع قرية البحر الأحمر', en: 'Training and Coral Reef Aquaculture for the Red Sea Village Project' },
    client: { ar: 'شركة الثريا العمرانية العقارية', en: 'Al-Thuraya Al-Umraniyah Real Estate Company' },
    region: { ar: 'جدة', en: 'Jeddah' },
    coords: [21.4858, 39.1925],
    summary: {
      ar: 'مشروع تدريب وتنفيذ لاستزراع الشعاب المرجانية ضمن مشروع قرية البحر الأحمر، يهدف إلى الحفاظ على التنوع البيولوجي البحري.',
      en: 'Coral reef training and cultivation programme at the Red Sea Village, aimed at preserving marine biodiversity.',
    },
  },
  {
    id: 7,
    name: { ar: 'تقييم الأثر البيئي لمحطة جدة البخارية للكهرباء', en: 'Environmental Impact Assessment (EIA) Study for the Jeddah Steam Power Plant Construction Project' },
    client: { ar: 'شركة هيونداي للصناعات الثقيلة', en: 'Hyundai Heavy Industries (HHI)' },
    region: { ar: 'جدة', en: 'Jeddah' },
    coords: [21.4858, 39.1925],
    summary: {
      ar: 'دراسة تقييم الأثر البيئي لمشروع إنشاء محطة جدة البخارية للكهرباء، مع وضع برنامج للرصد والإدارة البيئية.',
      en: 'EIA for the Jeddah Steam Power Plant, complete with monitoring and environmental management programmes.',
    },
  },
  {
    id: 8,
    name: { ar: 'تقييم الأثر البيئي لمشروع الجسر البري السعودي', en: 'Environmental Impact Assessment (EIA) Study for the Saudi Landbridge Project' },
    client: { ar: 'شركة البناء الصينية للهندسة المدنية', en: 'China Civil Engineering Construction Corporation (CCECC)' },
    region: { ar: 'الجبيل – الدمام – الرياض – جدة – ينبع', en: 'Jubail – Dammam – Riyadh – Jeddah – Yanbu' },
    coords: [24.7136, 46.6753],
    summary: {
      ar: 'دراسة بيئية شاملة لمشروع السكة الحديدية الرابطة بين شرق المملكة وغربها عبر خمس مدن رئيسية، مع تحليل الأثر التراكمي.',
      en: 'Comprehensive EIA for the trans-kingdom railway connecting east and west via five major cities, including cumulative-impact analysis.',
    },
  },
  {
    id: 9,
    name: { ar: 'إعداد دراسة تقييم الأثر البيئي لمشروع تحلية المياه', en: 'Preparing the Environmental Impact Assessment (EIA) Study for the Water Desalination Project' },
    client: { ar: 'شركة تقنيات المستقبل لتقنية المياه', en: 'Future Technologies for Water Technology Company' },
    region: { ar: 'الجافورة', en: 'Jafurah' },
    coords: [25.05, 49.05],
    summary: {
      ar: 'إعداد دراسة تقييم الأثر البيئي لمشروع تحلية مياه ضخم، شمل تقييم الأثر على البيئة البحرية والساحلية وخطط التخفيف.',
      en: 'EIA for a major desalination plant, covering marine and coastal impact assessment alongside mitigation planning.',
    },
  },
  {
    id: 10,
    name: { ar: 'دراسات تقييم الأثر البيئي لمنشآت جدة المركزية', en: 'Environmental Impact Assessment (EIA) Studies for the Jeddah Central Facilities' },
    client: { ar: 'دار الهندسة للاستشارات', en: 'Dar Al-Handasah Consultancy' },
    region: { ar: 'جدة', en: 'Jeddah' },
    coords: [21.5433, 39.1728],
    summary: {
      ar: 'سلسلة دراسات تقييم الأثر البيئي لعدد من المنشآت الحيوية في وسط مدينة جدة، تضمّنت دراسات الجودة، والضوضاء، والنفايات.',
      en: 'A series of EIA studies for vital facilities in central Jeddah — covering air quality, noise, and waste streams.',
    },
  },
];

const Projects = ({ theme = 'light' }) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const isDark = theme === 'dark';
  const [viewMode, setViewMode] = useState('map'); // 'map' | 'list'
  const [selected, setSelected] = useState(null);
  // When a region contains multiple projects, we open the region panel first
  // so the visitor can pick one — then the standard single-project panel takes over.
  const [selectedRegion, setSelectedRegion] = useState(null);

  // CMS-driven project list. Falls back to the static FALLBACK_PROJECTS if the
  // collection is empty, so first-time visitors always see the full portfolio.
  const [projects, setProjects] = useState(FALLBACK_PROJECTS);
  const [sc, setSc] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicContent('projects').then((items) => {
      if (!alive || !Array.isArray(items) || items.length === 0) return;
      const mapped = items
        .map((it) => {
          const m = it.metadata || {};
          const lat = Number(m.lat);
          const lng = Number(m.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return {
            id: it.id,
            image: it.image || '',
            name:    { ar: it.title_ar || it.title_en || '', en: it.title_en || it.title_ar || '' },
            client:  { ar: m.client_ar  || '', en: m.client_en  || m.client_ar  || '' },
            region:  { ar: m.region_ar  || '', en: m.region_en  || m.region_ar  || '' },
            summary: { ar: it.description_ar || m.summary_ar || '', en: it.description_en || m.summary_en || '' },
            coords: [lat, lng],
          };
        })
        .filter(Boolean);
      if (mapped.length > 0) setProjects(mapped);
    });
    fetchPublicSettings('section_copy').then((v) => { if (alive) setSc(v || {}); });
    return () => { alive = false; };
  }, []);
  const sec = makeSectionCopyPicker(sc, isAr);

  const PROJECTS = projects;

  // Group projects by region (bilingual key). When two or more projects share
  // the same region label we render a single marker for the region — clicking
  // it opens a compact list panel; otherwise it behaves like a normal marker.
  const REGIONS = useMemo(() => {
    const map = new Map();
    PROJECTS.forEach((p) => {
      const key = (p.region?.en || p.region?.ar || '').trim().toLowerCase() || `p-${p.id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          region: p.region,
          projects: [],
          _sumLat: 0,
          _sumLng: 0,
        });
      }
      const entry = map.get(key);
      entry.projects.push(p);
      entry._sumLat += p.coords[0];
      entry._sumLng += p.coords[1];
    });
    return Array.from(map.values()).map((r) => ({
      key: r.key,
      region: r.region,
      projects: r.projects,
      // Average of member coords — good enough for a single region pin without
      // over-engineering true clustering.
      coords: [r._sumLat / r.projects.length, r._sumLng / r.projects.length],
    }));
  }, [PROJECTS]);

  // Saudi Arabia + relevant southern/eastern neighbours. The frame is
  // deliberately shifted south so the initial view keeps the whole Kingdom
  // visible together with Yemen, Oman, UAE, the Gulf states, and NE Africa
  // (Egypt, Sudan, Eritrea) — while the top edge lands at Saudi's northern
  // border so Iraq, Iran, Jordan, Turkey and the Levant simply don't fit in
  // the initial view. Bounds are also the max-pan limits.
  const saudiBounds = useMemo(
    () => [
      [7.5, 28.0],  // SW: south-east of Eritrea / western Sudan
      [33.0, 60.0], // NE: Saudi's northern border / east coast of Oman
    ],
    []
  );
  // Center below Saudi's centroid so the map frame is tilted south — this is
  // what removes the northern countries from the initial view at zoom 6.
  const center = useMemo(() => [22.0, 45.0], []);

  // Approximate Saudi Arabia outline (simplified polygon — perfect for a subtle glowing border)
  // Stylised Saudi Arabia outline — clean clockwise polygon for premium visual focus
  // (removed: no large glowing country outline per design refresh)

  return (
    <section
      id="projects"
      dir={isAr ? 'rtl' : 'ltr'}
      className="relative py-32 overflow-hidden transition-colors duration-700"
    >
      {/* Warm cream/sand themed base — no dark navy */}
      <div className="absolute inset-0 bg-gradient-to-b from-amber-50 via-orange-50 to-amber-50 dark:from-amber-700/25 dark:via-orange-700/20 dark:to-amber-700/25" />

      {/* Soft warm halo accents */}
      <div className="absolute top-1/4 left-[10%] w-[450px] h-[450px] rounded-full blur-[120px] bg-brand-orange/15 pointer-events-none" />
      <div className="absolute bottom-1/4 right-[10%] w-[450px] h-[450px] rounded-full blur-[120px] bg-amber-300/20 pointer-events-none" />

      {/* Floating warm particles (very subtle) */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-brand-orange/30"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      <div className="relative container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 bg-brand-orange/10 dark:bg-brand-orange/25 border border-brand-orange/30 dark:border-brand-orange/50">
            <Sparkles className="w-4 h-4 text-brand-orange-700 dark:text-amber-200" />
            <span className="text-xs tracking-[0.3em] uppercase text-brand-orange-800 dark:text-amber-100 font-semibold" data-testid="projects-eyebrow">
              {sec('projects_eyebrow', isAr ? 'سجلّ الإنجازات' : 'Project Portfolio')}
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight" data-testid="projects-heading">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-orange-600 via-brand-orange-700 to-brand-orange-800 dark:from-amber-100 dark:via-amber-200 dark:to-orange-200">
              {sec('projects_heading', isAr ? 'مشاريعنا' : 'Our Projects')}
            </span>
          </h2>
          <p className="text-base md:text-lg text-slate-700 dark:text-amber-50 max-w-4xl mx-auto leading-relaxed" data-testid="projects-subtitle">
            {sec('projects_subtitle', isAr
              ? 'نفخر في شركة التوازن البيئي بسجل حافل من الإنجازات والمشاريع النوعية التي نفذناها بالتعاون مع كبرى الجهات الحكومية والشركات العالمية والمحلية. ومن أبرز مشاريعنا:'
              : 'At Al-Tawazun Al-Bee\'ie Company, we take pride in a proven track record of achievements and specialized projects executed in collaboration with leading government entities, as well as major international and local corporations. Among our featured projects:')}
          </p>
        </div>

        {/* View Toggle */}
        <div className="flex justify-center mb-8">
          <div
            className="inline-flex p-1 rounded-full bg-white/70 dark:bg-stone-900/40 border border-orange-200/70 dark:border-orange-400/30 backdrop-blur-md shadow-sm"
            data-testid="projects-view-toggle"
          >
            <button
              data-testid="projects-toggle-map"
              onClick={() => setViewMode('map')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 flex items-center gap-2 ${
                viewMode === 'map'
                  ? 'bg-gradient-to-r from-brand-orange to-brand-orange-700 text-white shadow-md shadow-orange-500/30'
                  : 'text-brand-orange-800 dark:text-amber-100 hover:bg-orange-50 dark:hover:bg-orange-900/30'
              }`}
            >
              <MapIcon className="w-4 h-4" />
              {isAr ? 'الخريطة' : 'Map'}
            </button>
            <button
              data-testid="projects-toggle-list"
              onClick={() => setViewMode('list')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 flex items-center gap-2 ${
                viewMode === 'list'
                  ? 'bg-gradient-to-r from-brand-orange to-brand-orange-700 text-white shadow-md shadow-orange-500/30'
                  : 'text-brand-orange-800 dark:text-amber-100 hover:bg-orange-50 dark:hover:bg-orange-900/30'
              }`}
            >
              <List className="w-4 h-4" />
              {isAr ? 'القائمة' : 'List'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto">
          {viewMode === 'map' ? (
            <div className="relative grid lg:grid-cols-12 gap-6 animate-fade-in">
              {/* Map */}
              <div
                className={`relative rounded-3xl overflow-hidden border border-orange-200/60
                            shadow-xl shadow-orange-500/10 bg-amber-50 ${
                              (selected || selectedRegion) ? 'lg:col-span-8' : 'lg:col-span-12'
                            }`}
                style={{ height: '620px' }}
                data-testid="projects-map"
              >
                <MapContainer
                  center={center}
                  zoom={6}
                  minZoom={6}
                  maxZoom={9}
                  maxBounds={saudiBounds}
                  maxBoundsViscosity={1.0}
                  scrollWheelZoom={true}
                  zoomControl={true}
                  worldCopyJump={false}
                  style={{ height: '100%', width: '100%', background: '#fdf6ec' }}
                  attributionControl={true}
                >
                  {/* MapTiler streets-v2 — accurate Middle East political rendering
                      (Saudi borders, Arabian Gulf naming, native Arabic + English
                      labels baked into the raster tiles). Language hint is
                      passed through so any future dynamic switching on the
                      MapTiler side is picked up automatically. Attribution is
                      kept per MapTiler's terms of service. Tile bounds are left
                      unset so tiles fill the whole container even after the
                      side panel toggles the grid layout. */}
                  <TileLayer
                    key={`maptiler-${isAr ? 'ar' : 'en'}`}
                    url={`https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${MAPTILER_KEY}&language=${isAr ? 'ar' : 'en'}`}
                    tileSize={512}
                    zoomOffset={-1}
                    crossOrigin={true}
                    attribution='&copy; <a href="https://www.maptiler.com/copyright/" target="_blank" rel="noreferrer">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>'
                  />

                  {/* Focus-mask — soft light-beige polygon covering everything
                      OUTSIDE the Saudi border. Country-name labels of Iraq,
                      Iran, Jordan, UAE, Yemen, Egypt, etc. get covered by this
                      overlay, while every label inside Saudi Arabia (cities,
                      provinces, roads) stays fully visible on the underlying
                      raster. Uses Leaflet's polygon-with-hole shape (outer
                      ring + Saudi outline as the hole). */}
                  <Polygon
                    positions={[WORLD_MASK_RING, SAUDI_OUTLINE]}
                    pathOptions={{
                      color: 'transparent',
                      weight: 0,
                      fillColor: '#fdf6ec',
                      fillOpacity: 0.98,
                      fillRule: 'evenodd',
                      interactive: false,
                    }}
                  />

                  {/* Recompute tile positions whenever the map container width
                      changes (side panel open/close), so no white strip appears. */}
                  <InvalidateOnResize trigger={!!selected || !!selectedRegion} />

                  {selected && <FlyTo position={selected.coords} zoom={8} />}
                  {!selected && selectedRegion && <FlyTo position={selectedRegion.coords} zoom={7} />}

                  {REGIONS.map((r) => {
                    const isActive =
                      (selectedRegion && selectedRegion.key === r.key) ||
                      (selected && r.projects.some((p) => p.id === selected.id));
                    return (
                      <Marker
                        key={r.key}
                        position={r.coords}
                        icon={createMarkerIcon(isActive, r.projects.length)}
                        eventHandlers={{
                          click: () => {
                            if (r.projects.length === 1) {
                              setSelectedRegion(null);
                              setSelected(r.projects[0]);
                            } else {
                              setSelected(null);
                              setSelectedRegion(r);
                            }
                          },
                        }}
                      >
                        <Tooltip
                            direction="auto"
                            offset={[0, -18]}
                            opacity={1}
                            className="ebc-tooltip"
                          >
                          <div
                            dir={isAr ? 'rtl' : 'ltr'}
                            style={{ minWidth: '200px', maxWidth: '280px' }}
                          >
                            <div
                              style={{
                                fontSize: '12px',
                                color: '#0891b2',
                                marginBottom: '4px',
                                fontWeight: 700,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                              }}
                            >
                              📍 {isAr ? (r.region?.ar || '') : (r.region?.en || '')}
                            </div>
                            {r.projects.length === 1 ? (
                              <>
                                <div
                                  style={{
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    marginBottom: '4px',
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {isAr ? r.projects[0].name.ar : r.projects[0].name.en}
                                </div>
                                <div style={{ fontSize: '11px', color: '#475569' }}>
                                  🏛️ {isAr ? r.projects[0].client.ar : r.projects[0].client.en}
                                </div>
                              </>
                            ) : (
                              <div
                                style={{
                                  fontSize: '12px',
                                  color: '#334155',
                                  fontWeight: 600,
                                }}
                              >
                                {isAr
                                  ? `${r.projects.length} مشاريع — اضغط للعرض`
                                  : `${r.projects.length} projects — click to view`}
                              </div>
                            )}
                          </div>
                        </Tooltip>
                      </Marker>
                    );
                  })}
                </MapContainer>

                {/* Subtle glow overlay */}
                <div className="pointer-events-none absolute inset-0 ring-1 ring-cyan-400/10 rounded-3xl" />
              </div>

              {/* Side Panel — Region list (when region has >1 project) */}
              {!selected && selectedRegion && (
                <div
                  className="lg:col-span-4 animate-fade-in-up"
                  data-testid="region-details-panel"
                >
                  <div className="relative h-full rounded-3xl overflow-hidden
                                bg-white/85 border border-orange-200/70
                                backdrop-blur-xl p-6 shadow-[0_20px_50px_-15px_rgba(245,135,32,0.25)]">
                    <button
                      data-testid="region-panel-close"
                      onClick={() => setSelectedRegion(null)}
                      className={`absolute top-4 ${isAr ? 'left-4' : 'right-4'} p-2 rounded-full bg-orange-50 hover:bg-orange-100 transition-colors border border-orange-200`}
                    >
                      <X className="w-5 h-5 text-brand-orange-700" />
                    </button>
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-brand-orange to-transparent" />

                    <div className={isAr ? 'text-right' : 'text-left'}>
                      <div className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-orange/10 border border-brand-orange/30 mb-4 ${isAr ? 'flex-row-reverse' : ''}`}>
                        <Layers className="w-4 h-4 text-brand-orange-700" />
                        <span className="text-sm font-semibold text-brand-orange-800" data-testid="region-panel-title">
                          {isAr ? selectedRegion.region?.ar : selectedRegion.region?.en}
                        </span>
                      </div>
                      <div className="text-xs uppercase tracking-[0.2em] text-brand-orange-700 font-semibold mb-3">
                        {isAr
                          ? `${selectedRegion.projects.length} مشاريع في هذه المنطقة`
                          : `${selectedRegion.projects.length} projects in this region`}
                      </div>

                      <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1 -mr-1">
                        {selectedRegion.projects.map((p) => {
                          const Chevron = isAr ? ChevronLeft : ChevronRight;
                          return (
                            <button
                              key={p.id}
                              data-testid={`region-project-item-${p.id}`}
                              onClick={() => setSelected(p)}
                              className={`group w-full text-${isAr ? 'right' : 'left'} rounded-2xl p-4
                                        bg-white/80 hover:bg-white border border-orange-200/70 hover:border-brand-orange/50
                                        transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md`}
                            >
                              <div className={`flex items-center gap-2 ${isAr ? 'flex-row-reverse' : ''}`}>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-bold text-slate-900 mb-1 leading-snug">
                                    {isAr ? p.name.ar : p.name.en}
                                  </div>
                                  <div className={`flex items-center gap-1.5 ${isAr ? 'flex-row-reverse' : ''}`}>
                                    <Building2 className="w-3 h-3 text-brand-orange/70 flex-shrink-0" />
                                    <span className="text-xs text-slate-600 truncate">
                                      {isAr ? p.client.ar : p.client.en}
                                    </span>
                                  </div>
                                </div>
                                <Chevron className="w-4 h-4 text-brand-orange-700/60 group-hover:text-brand-orange-700 flex-shrink-0" />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Side Panel — Single project details */}
              {selected && (
                <div
                  className="lg:col-span-4 animate-fade-in-up"
                  data-testid="project-details-panel"
                >
                  <div className="relative h-full rounded-3xl overflow-hidden
                                bg-white/85 border border-orange-200/70
                                backdrop-blur-xl p-6 shadow-[0_20px_50px_-15px_rgba(245,135,32,0.25)]">
                    {/* Close */}
                    <button
                      data-testid="project-panel-close"
                      onClick={() => {
                        // If the visitor arrived via a multi-project region panel,
                        // step back to that region list instead of clearing everything.
                        if (selectedRegion && selectedRegion.projects.length > 1) {
                          setSelected(null);
                        } else {
                          setSelected(null);
                          setSelectedRegion(null);
                        }
                      }}
                      className={`absolute top-4 ${
                        isAr ? 'left-4' : 'right-4'
                      } p-2 rounded-full bg-orange-50 hover:bg-orange-100 transition-colors border border-orange-200`}
                    >
                      <X className="w-5 h-5 text-brand-orange-700" />
                    </button>

                    {/* Top decorative bar */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-brand-orange to-transparent" />

                    <div className={isAr ? 'text-right' : 'text-left'}>
                      {/* Region badge */}
                      <div
                        className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-orange/10 border border-brand-orange/30 mb-5 ${
                          isAr ? 'flex-row-reverse' : ''
                        }`}
                      >
                        <MapPin className="w-4 h-4 text-brand-orange-700" />
                        <span className="text-sm font-semibold text-brand-orange-800">
                          {isAr ? selected.region.ar : selected.region.en}
                        </span>
                      </div>

                      {/* Project name */}
                      <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5 leading-snug">
                        {isAr ? selected.name.ar : selected.name.en}
                      </h3>

                      {/* Project image — fallback to default placeholder */}
                      {selected.image ? (
                        <div className="relative mb-6 rounded-2xl overflow-hidden h-44 border border-orange-200">
                          <img
                            src={selected.image}
                            alt={isAr ? selected.name.ar : selected.name.en}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="relative mb-6 rounded-2xl overflow-hidden h-44 bg-gradient-to-br from-amber-100 via-orange-100 to-amber-50 border border-orange-200 flex items-center justify-center">
                          <Building2 className="w-16 h-16 text-brand-orange/40" />
                          <div className="absolute inset-0 bg-gradient-to-t from-white/40 to-transparent" />
                        </div>
                      )}

                      {/* Client */}
                      <div className="mb-5">
                        <div className="text-xs uppercase tracking-[0.2em] text-brand-orange-700 font-semibold mb-1.5">
                          {isAr ? 'الجهة' : 'Client'}
                        </div>
                        <div className="text-base md:text-lg font-semibold text-slate-900 leading-snug">
                          {isAr ? selected.client.ar : selected.client.en}
                        </div>
                      </div>

                      {/* Summary */}
                      <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-brand-orange-700 font-semibold mb-2.5">
                          {isAr ? 'وصف مختصر' : 'Summary'}
                        </div>
                        <p
                          className="text-base md:text-lg text-slate-700 leading-loose"
                          style={{ direction: isAr ? 'rtl' : 'ltr' }}
                        >
                          {isAr ? selected.summary.ar : selected.summary.en}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // LIST VIEW
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 animate-fade-in" data-testid="projects-list">
              {PROJECTS.map((p, i) => (
                <article
                  key={p.id}
                  data-testid={`project-list-card-${p.id}`}
                  onClick={() => setSelected(p)}
                  className="group relative rounded-2xl p-6 overflow-hidden cursor-pointer
                           bg-white/85 border border-orange-200/70 hover:bg-white hover:border-brand-orange/50
                           backdrop-blur-md transition-all duration-300 hover:-translate-y-1
                           hover:shadow-xl hover:shadow-orange-500/15"
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-brand-orange/60 to-transparent" />

                  <div className={`flex mb-3 ${isAr ? 'justify-start' : 'justify-end'}`}>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-orange/10 border border-brand-orange/25">
                      <MapPin className="w-3 h-3 text-brand-orange-700" />
                      <span className="text-[11px] text-brand-orange-800 font-semibold">
                        {isAr ? p.region.ar : p.region.en}
                      </span>
                    </div>
                  </div>

                  <h3 className={`text-base lg:text-lg font-bold text-slate-900 mb-3 leading-snug ${isAr ? 'text-right' : 'text-left'}`}>
                    {isAr ? p.name.ar : p.name.en}
                  </h3>

                  <div className={`flex items-center gap-2 ${isAr ? 'flex-row-reverse text-right' : ''}`}>
                    <Building2 className="w-3.5 h-3.5 text-brand-orange/70 flex-shrink-0" />
                    <span className="text-xs text-slate-600 truncate">
                      {isAr ? p.client.ar : p.client.en}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Custom Leaflet styles — light warm theme */}
      <style>{`
        .leaflet-container { background: #fdf6ec !important; }
        .leaflet-control-zoom a {
          background: rgba(255, 255, 255, 0.95) !important;
          color: #b25e10 !important;
          border: 1px solid rgba(245, 135, 32, 0.3) !important;
          backdrop-filter: blur(8px);
        }
        .leaflet-control-zoom a:hover {
          background: #fef2e6 !important;
          color: #8a480c !important;
          border-color: rgba(245, 135, 32, 0.5) !important;
        }
        .ebc-marker { background: transparent !important; border: none !important; }
        .ebc-tooltip {
          background: rgba(255, 255, 255, 0.98) !important;
          border: 1px solid rgba(245, 135, 32, 0.35) !important;
          border-radius: 12px !important;
          padding: 10px 12px !important;
          box-shadow: 0 8px 24px rgba(245, 135, 32, 0.2) !important;
          color: #1c1917 !important;
          white-space: normal !important;
          max-width: 280px !important;
          width: max-content !important;
        }
        .ebc-tooltip::before { display: none !important; }
        .leaflet-tooltip { font-family: inherit !important; }
      `}</style>
    </section>
  );
};

export default Projects;
